/**
 * Admin & framework endpoints: User Management, My Profile, notification
 * settings, ngxh6 notifications / notes / history / filter forms / archive,
 * the /debug pages and the support widget.
 *
 * Exported for other surfaces (board, CEO report, AI assistant) so they show
 * the same numbers and records as these pages:
 *
 *   adminUsers()                         all dashboard accounts (mutable, in-tab)
 *   findUser(id) / userByEmail(email)    one account
 *   userJson(user, item?)                serialized User (item adds created/updated/createdBy/updatedBy)
 *   currentUserJson(email)               /api/v1/user/current body for any email (echoes it exactly;
 *                                        use for X-Switch-User impersonation of non-persona users)
 *   notificationsFor(persona)            persona's notifications; unreadNotificationCount(persona)
 *   ticketList(persona, limit?)          GET /api/support/tickets body for a persona
 *   supportTicketSummary()               org-wide counts {total, open, waitingOnSupport, waitingOnCustomer, inProgress, resolved}
 *   accessEntries()                      ngxh6 access log rows (last 48h)
 *   formView/field/choiceField/...       Symfony FormView builders (data/admin/forms.ts)
 *
 * Not routed here: /api/chat/message (the AI assistant, built in a later
 * wave), /api/myob/* (finance), /api/eh/* and /api/me/compliance (people).
 */
import { MockContext, Route, paginated, respond } from '../router';
import { accessEntries, accessEntryDetail, messengerMessages } from '../data/admin/debug';
import { emptyFormView, filterFormView, noteFormView, notificationSettingsFormView, profileFormView, saveNotificationSettings, saveProfile, saveUserForm, userFormView } from '../data/admin/user-forms';
import { markAllNotificationsRead, markNotificationRead, notificationsFor, unreadNotificationCount } from '../data/admin/notifications';
import { addTicketComment, createTicket, findTicket, isTicketDone, ticketDetail, ticketList } from '../data/admin/support';
import { findUser, notesFor, queryUsers, saveNote, userForPersona, userHistory, userJson } from '../data/admin/users';

export { adminUsers, currentUserJson, findUser, userByEmail, userJson, ROLE_LABELS } from '../data/admin/users';
export { notificationsFor, unreadNotificationCount } from '../data/admin/notifications';
export { supportTicketSummary, ticketList } from '../data/admin/support';
export { accessEntries } from '../data/admin/debug';
export { formView, field, choiceField, choice, textField, textareaField, hiddenField, lightswitchField, withError } from '../data/admin/forms';

const notFound = (what = 'Not found') => respond({ code: 404, message: what }, 404);

function userForm(ctx: MockContext) {
    const id = ctx.params['id'];
    const u = id ? findUser(id) : undefined;
    if (id && !u) {
        return notFound('User not found');
    }
    return userFormView(u, ctx.persona);
}

function postUserForm(ctx: MockContext) {
    const id = ctx.params['id'];
    const u = id ? findUser(id) : undefined;
    if (id && !u) {
        return notFound('User not found');
    }
    const res = saveUserForm(u, ctx.persona, ctx.body);
    return res.errors ? respond(res.errors, 400) : userJson(res.user!, true);
}

function archive(ctx: MockContext, state: boolean | null) {
    const u = findUser(ctx.params['id']);
    if (!u) {
        return { id: ctx.params['id'], archived: state ?? true };
    }
    u.archived = state === null ? !u.archived : state;
    return userJson(u, true);
}

function noteForm(ctx: MockContext) {
    return noteFormView(ctx.params['noteId'], ctx.query.get('objectId') || undefined);
}

function postNote(ctx: MockContext) {
    const message = String(ctx.body?.message ?? '').trim();
    if (!message) {
        const view = noteFormView(ctx.params['noteId'], ctx.body?.objectId);
        view.children['message'].vars['errors'] = ['This value should not be blank.'];
        return respond(view, 400);
    }
    return saveNote(ctx.persona, ctx.body, ctx.params['noteId']);
}

function notificationSettings(ctx: MockContext) {
    const u = (ctx.params['id'] && findUser(ctx.params['id'])) || userForPersona(ctx.persona);
    return notificationSettingsFormView(u);
}

function emptyList(ctx: MockContext) {
    return paginated([], ctx.query);
}

export const ADMIN_ROUTES: Route[] = [
    // ---------------------------------------------------------- users
    {
        method: 'GET',
        path: '/api/v1/users',
        handler: ctx => {
            const rows = queryUsers(ctx.query).map(u => userJson(u));
            return ctx.query.get('pagination') === 'false' ? rows : paginated(rows, ctx.query);
        },
    },
    {
        method: 'GET',
        path: '/api/v1/users/:id',
        handler: ctx => {
            const u = findUser(ctx.params['id']);
            return u ? userJson(u, true) : notFound('User not found');
        },
    },
    { method: 'GET', path: '/api/user/form', handler: userForm },
    { method: 'GET', path: '/api/user/form/:id', handler: userForm },
    { method: 'POST', path: '/api/user/form', handler: postUserForm },
    { method: 'POST', path: '/api/user/form/:id', handler: postUserForm },
    { method: 'GET', path: '/api/user/profile', handler: ctx => profileFormView(userForPersona(ctx.persona)) },
    {
        method: 'POST',
        path: '/api/user/profile',
        handler: ctx => {
            const res = saveProfile(userForPersona(ctx.persona), ctx.persona, ctx.body);
            return res.errors ? respond(res.errors, 400) : userJson(res.user!);
        },
    },

    // ---------------------------------------------------------- ngxh6 ext forms, filters, archive
    { method: 'GET', path: '/api/ext/form/notification-settings', handler: notificationSettings },
    { method: 'GET', path: '/api/ext/form/notification-settings/:id', handler: notificationSettings },
    {
        method: 'POST',
        path: '/api/ext/form/notification-settings/*',
        handler: ctx => {
            const u = (ctx.params['rest'] && findUser(ctx.params['rest'])) || userForPersona(ctx.persona);
            return userJson(saveNotificationSettings(u, ctx.body));
        },
    },
    { method: 'GET', path: '/api/ext/form/:slug', handler: ctx => emptyFormView(ctx.params['slug']) },
    { method: 'GET', path: '/api/ext/form/:slug/:id', handler: ctx => emptyFormView(ctx.params['slug']) },
    { method: 'POST', path: '/api/ext/form/:slug/*', handler: ctx => ({ id: ctx.params['rest'] || null, ...(ctx.body || {}) }) },
    { method: 'GET', path: '/api/form/*', handler: () => emptyFormView('form') },
    { method: 'GET', path: '/api/ext/filter/form/:cls', handler: ctx => filterFormView(ctx.params['cls']) },
    { method: 'GET', path: '/api/ext/filter/form/:cls/:slug', handler: ctx => filterFormView(ctx.params['cls']) },
    { method: 'POST', path: '/api/ext/toggle_archive/:id', handler: ctx => archive(ctx, null) },
    { method: 'POST', path: '/api/ext/archive/:id', handler: ctx => archive(ctx, true) },
    { method: 'POST', path: '/api/ext/unarchive/:id', handler: ctx => archive(ctx, false) },

    // ---------------------------------------------------------- notes + history (users, referrals, ...)
    { method: 'GET', path: '/api/v1/note_lists/:id/notes', handler: ctx => paginated(notesFor(ctx.params['id']), ctx.query) },
    { method: 'GET', path: '/api/v1/notes', handler: () => [] },
    { method: 'GET', path: '/api/notes/form', handler: noteForm },
    { method: 'GET', path: '/api/notes/form/:noteId', handler: noteForm },
    { method: 'POST', path: '/api/notes/form', handler: postNote },
    { method: 'POST', path: '/api/notes/form/:noteId', handler: postNote },
    {
        method: 'GET',
        path: '/api/v1/log_entries',
        handler: ctx => {
            const objectId = ctx.query.get('objectId');
            return paginated(objectId ? userHistory(objectId) : [], ctx.query);
        },
    },
    { method: 'POST', path: '/api/access_entry/audit_clicks', handler: () => ({}) },
    { method: 'GET', path: '/api/upload/sign', handler: () => ({}) },

    // ---------------------------------------------------------- notifications
    { method: 'GET', path: '/api/msg/notifications/count', handler: ctx => unreadNotificationCount(ctx.persona) },
    { method: 'GET', path: '/api/v1/notifications', handler: ctx => paginated(notificationsFor(ctx.persona), ctx.query) },
    {
        method: 'GET',
        path: '/api/v1/notifications/:id',
        handler: ctx => notificationsFor(ctx.persona).find(n => n.id === ctx.params['id']) || notFound('Notification not found'),
    },
    {
        method: 'POST',
        path: '/api/msg/notifications/mark-read/:id',
        handler: ctx => {
            markNotificationRead(ctx.persona, ctx.params['id']);
            return [];
        },
    },
    {
        method: 'POST',
        path: '/api/msg/notifications/mark-all-read',
        handler: ctx => {
            markAllNotificationsRead(ctx.persona);
            return [];
        },
    },
    { method: 'POST', path: '/api/msg/notifications/test', handler: () => ({}) },

    // ---------------------------------------------------------- support widget
    { method: 'GET', path: '/api/support/admin/status', handler: () => ({ connected: true, orgConfigured: true, auth: 'api-token', projectKey: 'SUP', serviceDeskId: '1' }) },
    { method: 'GET', path: '/api/support/tickets', handler: ctx => ticketList(ctx.persona, Number(ctx.query.get('limit') || 20)) },
    {
        method: 'POST',
        path: '/api/support/tickets',
        handler: ctx => {
            if (!String(ctx.body?.summary || '').trim() || !String(ctx.body?.description || '').trim()) {
                return respond({ error: 'Summary and description are required' }, 400);
            }
            return respond(createTicket(ctx.persona, ctx.body), 201);
        },
    },
    {
        method: 'GET',
        path: '/api/support/tickets/:key',
        handler: ctx => {
            const t = findTicket(ctx.params['key']);
            return t && t.reporterEmail === ctx.persona.email ? ticketDetail(t) : notFound('Ticket not found');
        },
    },
    {
        method: 'POST',
        path: '/api/support/tickets/:key/comments',
        handler: ctx => {
            const t = findTicket(ctx.params['key']);
            const body = String(ctx.body?.body || '').trim();
            if (!t) {
                return notFound('Ticket not found');
            }
            if (!body) {
                return respond({ error: 'Comment body is required' }, 400);
            }
            return respond(addTicketComment(ctx.persona, t, body), 201);
        },
    },
    {
        method: 'POST',
        path: '/api/support/tickets/:key/confirm',
        handler: ctx => {
            const t = findTicket(ctx.params['key']);
            if (!t) {
                return notFound('Ticket not found');
            }
            if (!isTicketDone(t)) {
                return respond({ error: 'Ticket is not resolved yet' }, 409);
            }
            t.confirmed = true;
            return { confirmed: true };
        },
    },
    { method: 'POST', path: '/api/support/tickets/:key/attachments', handler: () => respond({ temporaryAttachments: [], attachments: { values: [] } }, 201) },

    // ---------------------------------------------------------- ngxh6 /debug pages
    { method: 'GET', path: '/api/v1/access_entries', handler: ctx => paginated(accessEntries(), ctx.query) },
    { method: 'GET', path: '/api/v1/access_entries/:id', handler: ctx => accessEntryDetail(ctx.params['id']) || notFound('Access entry not found') },
    { method: 'GET', path: '/api/v1/messenger_messages', handler: ctx => paginated(messengerMessages(), ctx.query) },
    { method: 'GET', path: '/api/v1/messenger_messages/:id', handler: () => notFound('Message not found') },
    { method: 'POST', path: '/api/queue/cancel/:id', handler: () => ({}) },
    { method: 'POST', path: '/api/queue/retry/:id', handler: () => ({}) },
    { method: 'POST', path: '/api/queue/cancel-all', handler: () => ({}) },
    { method: 'POST', path: '/api/queue/retry-all', handler: () => ({}) },
    { method: 'POST', path: '/api/queue/clean-up', handler: () => ({}) },
    { method: 'GET', path: '/api/v1/key_values', handler: emptyList },
    { method: 'GET', path: '/api/v1/key_values/:id', handler: ctx => ({ id: ctx.params['id'] }) },
    { method: 'GET', path: '/api/v1/examples', handler: emptyList },
    { method: 'GET', path: '/api/v1/examples/:id', handler: ctx => ({ id: ctx.params['id'] }) },
    { method: 'GET', path: '/api/v1/example_categories', handler: emptyList },
    { method: 'GET', path: '/api/v1/example_categories/:id', handler: ctx => ({ id: ctx.params['id'] }) },
];
